import { useNavigate } from "react-router-dom";

import { Button } from "components/ui/Button";
import { Notice, NoticeText } from "components/ui/Notice";
import { pathGenerators } from "utils/routes";

/**
 * "This will be recorded, but it will not be paid."
 *
 * PCRS requires a PPSN on every reimbursement claim, so a vaccination for a patient
 * without one is reported to NIIS and the claim is withheld — the record is legally
 * complete and financially unfinished, and nothing on the form said so until now
 * (KIN-1663).
 *
 * Deliberately not a blocker. Infants on a PCI course routinely have no PPSN yet and
 * must still be vaccinated and reported; the same is true of the walk-in this practice
 * cannot turn away. `HseDetailsNotice` is the blocking one, for details without which
 * no message can be built at all.
 *
 * It wears the red blocking tone even so, as `HseEntitlementNotice` beside it does — see the note
 * there. Three red boxes can stand on this form at once and only one of them withholds Save, which
 * reads as a bug and has been reported as one. Changing the tone was tried and reverted: the red
 * was wanted. Worth revisiting as a pair, never one of them alone.
 *
 * Renders nothing until the patient has loaded: a warning that appears a beat after the
 * form does is one people learn to dismiss.
 */
export const NoPpsnClaimNotice = ({ patient, patientId }) => {
  const navigate = useNavigate();

  if (!patient) return null;

  const hasPpsn = !!patient.nationalNumber?.trim();

  if (hasPpsn) return null;

  const editablePatientId = patientId ?? patient.id;

  return (
    <Notice
      testId="no-ppsn-claim-notice"
      action={
        !!editablePatientId && (
          <Button
            variant="outline"
            size="small"
            onClick={() =>
              navigate(pathGenerators.patient.details(editablePatientId))
            }
          >
            Edit patient details
          </Button>
        )
      }
    >
      <NoticeText>
        This patient has no PPSN recorded. The vaccination will still be
        reported to NIIS, but no reimbursement claim will be sent until a PPSN
        is added to the patient record.
      </NoticeText>
    </Notice>
  );
};
